import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import { Layout } from "@/components/layout";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { CalendarIcon, ChevronLeft, Edit, FileText, Loader2, Mail, MapPin, Phone, RefreshCw, Trash, User } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

// Agreement form schema
const agreementFormSchema = z.object({
  businessName: z.string().min(1, "Business name is required"),
  businessAddress: z.string().min(1, "Business address is required"),
  contractorName: z.string().min(1, "Contractor name is required"),
  contractorAddress: z.string().min(1, "Contractor address is required"),
  serviceDescription: z.string().min(1, "Service description is required"),
  startDate: z.date(),
  endDate: z.date().optional().nullable(),
  paymentTerms: z.string().min(1, "Payment terms are required"),
  paymentRate: z.string().min(1, "Payment rate is required"),
  additionalTerms: z.string().optional().nullable(),
});

type AgreementFormValues = z.infer<typeof agreementFormSchema>;

export default function ContractorDetailsPage() {
  const params = useParams<{ id: string }>();
  const [_, navigate] = useLocation();
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  const queryClient = useQueryClient();
  const [isGeneratingAgreement, setIsGeneratingAgreement] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const contractorId = parseInt(params.id);
  const businessId = currentBusiness?.id;
  
  // Fetch contractor details
  const { 
    data: contractor,
    isLoading: isLoadingContractor,
    error: contractorError
  } = useQuery({
    queryKey: [`/api/businesses/${businessId}/contractors`, contractorId],
    queryFn: async () => {
      if (!businessId) return null;
      const res = await apiRequest("GET", `/api/businesses/${businessId}/contractors/${contractorId}`);
      return res.json();
    },
    enabled: !!businessId && !!contractorId
  });
  
  // Fetch legal document templates
  const {
    data: templates,
    isLoading: isLoadingTemplates
  } = useQuery({
    queryKey: ["/api/legal-document-templates/type/independent_contractor"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/legal-document-templates/type/independent_contractor");
      return res.json();
    }
  });
  
  // Fetch generated agreements for this contractor
  const {
    data: agreements,
    isLoading: isLoadingAgreements
  } = useQuery({
    queryKey: ["/api/contractors", contractorId, "agreements"],
    queryFn: async () => {
      if (!businessId) return [];
      const res = await apiRequest("GET", `/api/businesses/${businessId}/legal-documents?contractorId=${contractorId}`);
      return res.json();
    },
    enabled: !!businessId && !!contractorId
  });
  
  // Delete contractor mutation
  const deleteContractorMutation = useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error("No business selected");
      const res = await apiRequest("DELETE", `/api/businesses/${businessId}/contractors/${contractorId}`);
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Contractor deleted",
        description: "The contractor has been deleted successfully"
      });
      navigate("/expenses/contractors");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete contractor",
        variant: "destructive"
      });
    }
  });
  
  // Form to generate agreement
  const form = useForm<AgreementFormValues>({
    resolver: zodResolver(agreementFormSchema),
    defaultValues: {
      businessName: currentBusiness?.name || "",
      businessAddress: "",
      contractorName: contractor?.name || "",
      contractorAddress: "",
      serviceDescription: "",
      startDate: new Date(),
      endDate: null,
      paymentTerms: "Net 30",
      paymentRate: "",
      additionalTerms: ""
    }
  });
  
  // Update form values when contractor data loads
  React.useEffect(() => {
    if (contractor) {
      form.setValue("contractorName", contractor.name);
      
      // If contractor has address
      if (contractor.address) {
        const addressObj = typeof contractor.address === 'string' 
          ? JSON.parse(contractor.address) 
          : contractor.address;
          
        const formattedAddress = [
          addressObj.street,
          addressObj.city,
          addressObj.state,
          addressObj.zipCode,
          addressObj.country
        ].filter(Boolean).join(", ");
        
        form.setValue("contractorAddress", formattedAddress);
      }
      
      if (contractor.serviceType) {
        form.setValue("serviceDescription", contractor.serviceType);
      }
      
      if (contractor.contractStart) {
        form.setValue("startDate", new Date(contractor.contractStart));
      }
      
      if (contractor.contractEnd) {
        form.setValue("endDate", new Date(contractor.contractEnd));
      }
      
      if (contractor.paymentRate) {
        form.setValue("paymentRate", `$${contractor.paymentRate} per hour`);
      }
      
      if (contractor.paymentTerms) {
        form.setValue("paymentTerms", contractor.paymentTerms);
      }
    }
    
    if (currentBusiness) {
      form.setValue("businessName", currentBusiness.name);
      
      // Try to get business address if available
      if (currentBusiness.address) {
        form.setValue("businessAddress", currentBusiness.address);
      }
    }
  }, [contractor, currentBusiness, form]);
  
  // Generate agreement mutation
  const generateAgreementMutation = useMutation({
    mutationFn: async (data: AgreementFormValues) => {
      if (!businessId) throw new Error("No business selected");
      if (!templates || templates.length === 0) throw new Error("No templates available");
      
      const templateId = templates[0].id;
      const documentName = `${data.contractorName} - Independent Contractor Agreement`;
      
      const res = await apiRequest("POST", "/api/legal-documents", {
        templateId,
        documentName,
        formData: {
          ...data,
          contractorId
        }
      });
      
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Agreement generated",
        description: "The contractor agreement has been generated successfully"
      });
      setIsGeneratingAgreement(false);
      queryClient.invalidateQueries({ queryKey: ["/api/contractors", contractorId, "agreements"] });
      
      // Navigate to view the generated document
      navigate(`/legal/documents/${data.id}`);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to generate agreement",
        variant: "destructive"
      });
    }
  });
  
  const onSubmitGenerateAgreement = (data: AgreementFormValues) => {
    generateAgreementMutation.mutate(data);
  };
  
  // Handle delete contractor
  const handleDelete = () => {
    deleteContractorMutation.mutate();
    setIsDeleteDialogOpen(false);
  };
  
  if (isLoadingContractor) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }
  
  if (contractorError || !contractor) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <h2 className="text-2xl font-bold mb-2">Contractor Not Found</h2>
          <p className="text-muted-foreground mb-4">The contractor you're looking for doesn't exist or you don't have access.</p>
          <Button onClick={() => navigate("/expenses/contractors")}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Contractors
          </Button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      {/* Agreement Generation Dialog */}
      <Dialog open={isGeneratingAgreement} onOpenChange={setIsGeneratingAgreement}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Generate Contractor Agreement</DialogTitle>
            <DialogDescription>
              Fill in the details below to generate an Independent Contractor Agreement.
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitGenerateAgreement)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="businessName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="contractorName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contractor Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="businessAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="contractorAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contractor Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="serviceDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description of Services</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Start Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className="w-full pl-3 text-left font-normal flex justify-between"
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>End Date (Optional)</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className="w-full pl-3 text-left font-normal flex justify-between"
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value || undefined}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="paymentRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Rate</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. $50 per hour" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="paymentTerms"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Terms</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Net 30" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="additionalTerms"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Additional Terms (Optional)</FormLabel>
                    <FormControl>
                      <Textarea {...field} value={field.value || ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsGeneratingAgreement(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={generateAgreementMutation.isPending}
                >
                  {generateAgreementMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    "Generate Agreement"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Contractor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {contractor.name}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteContractorMutation.isPending}
            >
              {deleteContractorMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Contractor Details */}
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate("/expenses/contractors")}
              className="mr-4"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold">{contractor.name}</h1>
            <Badge 
              variant={contractor.status === "active" ? "default" : "secondary"}
              className="ml-4"
            >
              {contractor.status === "active" ? "Active" : "Inactive"}
            </Badge>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={() => setIsGeneratingAgreement(true)}>
              <FileText className="h-4 w-4 mr-2" />
              Generate Agreement
            </Button>
            <Button variant="outline">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Contractor Information */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Contractor Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Contact Information</h3>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{contractor.name}</span>
                    </div>
                    {contractor.email && (
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{contractor.email}</span>
                      </div>
                    )}
                    {contractor.phone && (
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{contractor.phone}</span>
                      </div>
                    )}
                    {contractor.address && (
                      <div className="flex items-start">
                        <MapPin className="h-4 w-4 mr-2 text-muted-foreground mt-1" />
                        <span>
                          {typeof contractor.address === 'string' 
                            ? contractor.address 
                            : [
                                contractor.address.street,
                                contractor.address.city,
                                contractor.address.state,
                                contractor.address.zipCode,
                                contractor.address.country
                              ].filter(Boolean).join(", ")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Tax Information</h3>
                  <div className="space-y-2">
                    {contractor.taxId && (
                      <div className="flex items-center">
                        <span className="font-medium mr-2">Tax ID:</span>
                        <span>{contractor.taxId}</span>
                      </div>
                    )}
                    {contractor.ssn && (
                      <div className="flex items-center">
                        <span className="font-medium mr-2">SSN:</span>
                        <span>{contractor.ssn}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Contract Details</h3>
                  <div className="space-y-2">
                    {contractor.serviceType && (
                      <div className="flex flex-col">
                        <span className="font-medium">Services:</span>
                        <span>{contractor.serviceType}</span>
                      </div>
                    )}
                    
                    <div className="flex flex-col">
                      <span className="font-medium">Contract Period:</span>
                      <span>
                        {contractor.contractStart ? format(new Date(contractor.contractStart), "PPP") : "Not specified"} 
                        {contractor.contractEnd ? ` to ${format(new Date(contractor.contractEnd), "PPP")}` : ""}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">Payment Information</h3>
                  <div className="space-y-2">
                    {contractor.paymentRate && (
                      <div className="flex items-center">
                        <span className="font-medium mr-2">Rate:</span>
                        <span>${contractor.paymentRate}</span>
                      </div>
                    )}
                    {contractor.paymentTerms && (
                      <div className="flex items-center">
                        <span className="font-medium mr-2">Terms:</span>
                        <span>{contractor.paymentTerms}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {contractor.notes && (
                <>
                  <Separator />
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">Notes</h3>
                    <p className="text-sm">{contractor.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Agreements */}
          <Card>
            <CardHeader>
              <CardTitle>Agreements</CardTitle>
              <CardDescription>Generated legal agreements for this contractor</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingAgreements ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : agreements && agreements.length > 0 ? (
                <ul className="space-y-3">
                  {agreements.map((agreement: any) => (
                    <li key={agreement.id} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                        <span>{agreement.documentName}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/legal/documents/${agreement.id}`)}
                      >
                        View
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No agreements generated yet</p>
                  <Button 
                    onClick={() => setIsGeneratingAgreement(true)}
                    variant="outline" 
                    size="sm"
                    className="mt-2"
                  >
                    Generate Agreement
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}